import {
  Box,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  Spacer,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text as ChakraText,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  StackDivider,
  Center,
  Badge,
  Grid,
  GridItem,
  useClipboard,
} from '@chakra-ui/react'
import _ from 'lodash'
import moment from 'moment'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa'
import type { CellProps, Column } from 'react-table'
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table'
import useFetch, { CachePolicies } from 'use-http'
import styled from 'styled-components'
import { vs2015 } from 'react-syntax-highlighter/dist/cjs/styles/hljs'
import { CopyIcon } from '@chakra-ui/icons'
import { BaseLayout } from '..'
import { colors } from '../../../ui-kit'
import PageNavigator from '../../components/Pagination/PageNavigator'
import type {
  AuditLogs,
  AuditLogsRequest,
  AuditLogsResponse,
  AuditLogsErrorResponse,
  ListAllUsersInLogsResponse,
  ListAllActiveUsersResponse,
} from '../../interfaces'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'
import useToast from '../../hooks/useToast'
import { UserContext } from '../../providers'
import { DateSelector } from '../../components/Analytics'
import { OperationsEnum } from '../../../utils/operations'
import { ReactSyntaxHighlighter } from '../../components/ReactSyntaxHighlighter'
import MultiSelectMenu from '../../components/AuditLogs/MultiSelectMenu'
import CustomPlanCTA from '../../components/CustomPlan'
import { FeaturesEnum } from '../../../utils/features'
import PermissionGuard from '../../components/PermissionGuard'
import Hover from '../../components/Hover'
import Pagination from '../../components/Pagination'

const codeBlockStyles = {
  backgroundColor: '#193055',
  padding: 30,
  lineHeight: '20px',
  fontSize: 14,
  marginRight: '40px',
}

const ReactSyntaxHighlighterWrapper = styled(ReactSyntaxHighlighter)`
  .react-syntax-highlighter-line-number {
    color: #888 !important;
    padding-left: 0.5em !important;
    padding-right: 1.5em !important;
    min-width: 2.25em !important;
  }
  pre {
    margin: 0px !important;
  }
  code {
    span {
      font-family: monospace, monospace !important;
    }
  }
`
const subtractDates = (date1, date2): number => {
  const diffTime = Math.abs(date2 - date1)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays
}

const StatusBadge = ({
  statusCode,
  fontSize,
  fontWeight,
}: {
  statusCode: number
  fontSize?: string
  fontWeight?: string
}): JSX.Element => {
  let colorScheme = 'green'
  let statusCodeBadgeText = `${statusCode}`
  if (statusCode === 200) {
    colorScheme = 'green'
    statusCodeBadgeText = `${statusCode} OK`
  } else if (statusCode > 200 && statusCode < 400) {
    colorScheme = 'green'
    statusCodeBadgeText = `${statusCode}`
  } else if (statusCode >= 400 && statusCode < 500) {
    colorScheme = 'yellow'
    statusCodeBadgeText = `${statusCode} ERR`
  } else if (statusCode >= 500) {
    colorScheme = 'red'
    statusCodeBadgeText = `${statusCode} ERR`
  }

  return (
    <Badge
      fontSize={fontSize}
      colorScheme={colorScheme}
      fontWeight={fontWeight}
    >
      {statusCodeBadgeText}
    </Badge>
  )
}

const AuditLogsLayout = (): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const [logs, setLogs] = useState<AuditLogs[]>([])
  const [users, setUsers] = useState<ListAllUsersInLogsResponse[]>()
  const { user } = useContext(UserContext)
  const initialEndDate = moment().endOf('day').toDate()
  const initialStartDate = moment().startOf('day').toDate()
  const minStartDate = moment().subtract(364, 'day').toDate()
  const [disableApply, setDisableApply] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [startDate, setStartDate] = useState<Date>(initialStartDate)
  const [endDate, setEndDate] = useState<Date>(initialEndDate)
  const [userList, setUserList] = useState<string[]>([])
  const [operations, setOperations] = useState<string[]>([])
  const [dateError, setDateError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [offset, setOffset] = useState<number>(0)
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0)
  const [controlledPageCount, setControlledPageCount] = useState<number>(0)
  const [logDisplayIndex, setLogDisplayIndex] = useState<number>(0)
  const [copyRequestId, setCopyRequestId] = useState<string>('')
  const { onCopy } = useClipboard(copyRequestId)
  const DATE_12_MONTHS_ERROR = useMemo(
    () => t('date_365_error', 'You can select a maximum range of 12 months!'),
    [],
  )
  const DATE_BAD_ORDER_ERROR = useMemo(
    () => t('date_bad_error', 'Start date cannot be greater than the end date'),
    [],
  )

  const { request: requestLogs, loading: loadingLogs = false } =
    useFetch<AuditLogsRequest>({
      cachePolicy: CachePolicies.NO_CACHE,
    })
  const { request: requestUsers } = useFetch<
    ListAllActiveUsersResponse[]
  >({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const { request: requestAllUsers } = useFetch<
    ListAllUsersInLogsResponse[]
  >({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getLogs = async (pageOffset: number): Promise<void> => {
    setOffset(pageOffset)
    setIsRefreshing(true)
    setDisableApply(true)
    if (
      user?.clientNumber &&
      user.pricingPlan.featuresList.includes(FeaturesEnum.CENTRAL_AUDIT_LOGS)
    ) {
      const response = (await requestLogs.post(
        `/api/clients/${user.clientNumber}/audit-logs/search`,
        {
          userIds: userList,
          operations,
          timeInterval: {
            start: startDate,
            end: endDate,
          },
          limit: pageSize,
          offset: pageOffset,
        },
      )) as unknown as AuditLogsResponse | AuditLogsErrorResponse

      if (requestLogs.error) {
        toast({
          description: t('something_went_wrong', 'Something went wrong!'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        setControlledPageCount(
          (response as AuditLogsResponse).paging.totalPages,
        )
        setTotalCount((response as AuditLogsResponse).paging.totalCount)
        setCurrentPageIndex(
          (response as AuditLogsResponse).paging.currentPageIndex,
        )
        setLogs((response as AuditLogsResponse).logs)
      }
    }
    setIsRefreshing(false)
    setDisableApply(false)
  }

  const getUsers = async (): Promise<void> => {
    if (
      user?.clientNumber &&
      user.pricingPlan.featuresList.includes(FeaturesEnum.CENTRAL_AUDIT_LOGS)
    ) {
      const allUsersInLogsList = await requestAllUsers.get(
        `/api/clients/${user.clientNumber}/audit-logs/all-users`,
      )
      const currentUsersList: ListAllActiveUsersResponse[] = await requestUsers.get(
        `/api/clients/${user.clientNumber}/audit-logs/all-active-users`,
      )
      if (requestAllUsers.error || requestUsers.error) {
        toast({
          description: t('something_went_wrong', 'Something went wrong!'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return
      }

      const updatedAllUsersList = allUsersInLogsList?.map(userItem => {
        if (currentUsersList) {
          const currentUser = currentUsersList.find(
            // eslint-disable-next-line no-underscore-dangle
            currentUserItem => currentUserItem._id === userItem._id,
          )
          if (!currentUser) {
            userItem.isDeleted = true
            return userItem
          }
          userItem.userName = currentUser.userName
        }
        return userItem
      })
      setUsers(updatedAllUsersList)
    }
  }

  useEffect(() => {
    void getUsers()
  }, [])

  useEffect(() => {
    if (copyRequestId) {
      onCopy()
      toast({
        description: t(
          'copied_request_id_to_clipboard',
          'Copied Request ID to clipboard',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    }
  }, [copyRequestId])

  useEffect(() => {
    if (startDate > endDate) {
      setDateError(DATE_BAD_ORDER_ERROR)
      setDisableApply(true)
      return
    }

    const difference = subtractDates(startDate, endDate)
    if (difference > 365) {
      setDateError(DATE_12_MONTHS_ERROR)
      setDisableApply(true)
      return
    }
    setDateError(null)
    setDisableApply(false)
  }, [startDate, endDate])

  const handleSubmit = useCallback(() => {
    void getLogs(0)
  }, [startDate, endDate, userList, operations])

  const columns = useMemo<Array<Column<AuditLogs>>>(
    () => [
      {
        Header: t('status', 'Status').toString(),
        accessor: values => values.statusCode,
        Cell: ({ value }: CellProps<AuditLogs, number>) => (
          <StatusBadge statusCode={value} />
        ),
        width: '120px',
      },
      {
        Header: t('operation', 'Operation').toString(),
        accessor: values => values.operation,
        Cell: ({ value }: CellProps<AuditLogs, string>) => (
          <Tooltip label={value}>
            <ChakraText maxW="150px" isTruncated>
              {value}
            </ChakraText>
          </Tooltip>
        ),
      },
      {
        Header: t('user', 'User').toString(),
        accessor: values => {
          // eslint-disable-next-line no-underscore-dangle
          const userToDisplay = users?.find(obj => obj._id === values.userId)
          if (userToDisplay) {
            return userToDisplay
          }
          return { userName: 'Unknown', userEmail: 'Unknown' }
        },
        width: '120px',
        Cell: ({ value }: CellProps<AuditLogs, ListAllUsersInLogsResponse>) => (
          <Tooltip
            label={
              <>
                <>
                  {`Name: ${value.userName}`}
                  <ChakraText
                    ml={1}
                    as="span"
                    fontStyle="italic"
                    color="#ced1d6"
                  >
                    {value.isDeleted ? '(deleted)' : ''}
                  </ChakraText>
                </>
                <ChakraText>{`Email: ${value.userEmail}`}</ChakraText>
              </>
            }
          >
            <Flex
              maxW="150px"
              alignItems="center"
              justifyContent="space-between"
            >
              <ChakraText maxW={value.isDeleted ? '70%' : '100%'} isTruncated>
                {`${value.userName}`}
              </ChakraText>
              <ChakraText
                ml={1}
                fontStyle="italic"
                color="appBattleshipGrey"
                fontSize="xs"
              >
                {value.isDeleted ? '(deleted)' : ''}
              </ChakraText>
            </Flex>
          </Tooltip>
        ),
      },
      {
        Header: t('timestamp', 'Time').toString(),
        accessor: values => values.timeStamp,
        Cell: ({ value }: CellProps<AuditLogs, string>) => (
          <Tooltip
            label={
              <>
                <ChakraText>
                  {moment(value).local().format('ddd, MMMM Do YYYY, HH:mm:ss')}{' '}
                  (local)
                </ChakraText>
                <ChakraText>
                  {moment.utc(value).format('ddd, MMMM Do YYYY, HH:mm:ss')}{' '}
                  (UTC)
                </ChakraText>
              </>
            }
          >
            <ChakraText>
              {moment(value).local().format('ddd, MMMM Do YYYY, HH:mm:ss')}
            </ChakraText>
          </Tooltip>
        ),
      },
    ],
    [users],
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    state: { pageIndex, pageSize, ...tableState },
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions,
    setPageSize,
    previousPage,
    nextPage,
    gotoPage,
  } = useTable<AuditLogs>(
    {
      columns,
      data: logs,
      initialState: {
        pageIndex: currentPageIndex,
        sortBy: [
          {
            id: 'Time',
            desc: true,
          },
        ],
      },
      manualPagination: true,
      pageCount: controlledPageCount,
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  )

  useEffect(() => {
    void getLogs(pageIndex * pageSize)
  }, [pageSize, pageIndex])

  return (
    <BaseLayout>
      <MainContentHeadingBox>
        <Stack direction={['column']} spacing={1}>
          <Heading
            as="h1"
            size="md"
            fontWeight="semibold"
            color="appDarkIndigo"
          >
            {t('audit_logs', 'Audit logs')}
          </Heading>
          <ChakraText>
            {t(
              'audit_logs_page_subheading',
              "You can view logs' details upto past 12 months",
            )}
          </ChakraText>
        </Stack>
      </MainContentHeadingBox>
      <MainContentWrapper style={{ gap: '0.5em' }}>
        {user?.pricingPlan.featuresList.includes(
          FeaturesEnum.CENTRAL_AUDIT_LOGS,
        ) ? (
          <>
            <Flex align="center" gap="15px">
              {users && (
                <MultiSelectMenu
                  label={t('select_users', 'Select users')}
                  options={users.map(option => ({
                    label: (
                      <Tooltip
                        label={
                          <>
                            <>
                              {`Name: ${option.userName}`}
                              <ChakraText
                                ml={1}
                                as="span"
                                fontStyle="italic"
                                color="#ced1d6"
                              >
                                {option.isDeleted ? '(deleted)' : ''}
                              </ChakraText>
                            </>
                            <ChakraText>{`Email: ${option.userEmail}`}</ChakraText>
                          </>
                        }
                      >
                        <Flex
                          maxW="250px"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <ChakraText
                            maxW={option.isDeleted ? '70%' : '100%'}
                            isTruncated
                          >{`${option.userName}`}</ChakraText>
                          <ChakraText
                            ml={1}
                            fontStyle="italic"
                            color="appBattleshipGrey"
                            fontSize="xs"
                          >
                            {option.isDeleted ? '(deleted)' : ''}
                          </ChakraText>
                        </Flex>
                      </Tooltip>
                    ),
                    // eslint-disable-next-line no-underscore-dangle
                    value: String(option._id),
                  }))}
                  onChange={selectedValues => {
                    setUserList(selectedValues)
                  }}
                />
              )}

              <MultiSelectMenu
                label={t('select_operations', 'Select operations')}
                options={Object.values(OperationsEnum).map(option => ({
                  label: <ChakraText>{String(option)}</ChakraText>,
                  value: String(option),
                }))}
                onChange={selectedValues => {
                  setOperations(selectedValues)
                }}
              />

              <DateSelector
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                handleDateSubmit={handleSubmit}
                disabled={disableApply || isRefreshing}
                isRefreshing={isRefreshing}
                dateError={dateError}
                minStartDate={minStartDate}
                width="180px"
              />
            </Flex>
            <Divider mt={5} />
            <HStack
              overflow="hidden"
              alignItems="flex-start"
              divider={
                <StackDivider borderColor="gray.200" alignSelf="stretch" />
              }
            >
              <VStack
                alignItems="flex-start"
                paddingRight={4}
                paddingTop={4}
                w="100%"
              >
                <Flex maxH="65vh" overflow="scroll" minWidth="100%">
                  <Table {...getTableProps}>
                    <Thead bgColor="appPaleGreyTwo">
                      {headerGroups.map(headerGroup => {
                        const { key, ...restHeaderGroupProps } =
                          headerGroup.getHeaderGroupProps()
                        return (
                          <Tr key={key} {...restHeaderGroupProps}>
                            {headerGroup.headers.map((column, i) => {
                              const { key: keyCol, ...restColumn } =
                                column.getHeaderProps(
                                  column.getSortByToggleProps(),
                                )
                              return (
                                <Th
                                  key={keyCol}
                                  {...restColumn}
                                  width={column.width}
                                >
                                  {column.render('Header')}
                                  {!column.disableSortBy ? (
                                    <Box
                                      as="span"
                                      pl="2"
                                      color={colors.appBattleshipGrey}
                                    >
                                      {column.isSorted ? (
                                        column.isSortedDesc ? (
                                          <Icon
                                            as={FaSortUp}
                                            aria-label={t(
                                              'sorted_descending',
                                              'sorted descending',
                                            )}
                                          />
                                        ) : (
                                          <Icon
                                            as={FaSortDown}
                                            aria-label={t(
                                              'sorted_ascending',
                                              'sorted ascending',
                                            )}
                                          />
                                        )
                                      ) : (
                                        <Icon
                                          as={FaSort}
                                          aria-label={t(
                                            'sort_by_column',
                                            'sort by column',
                                          )}
                                        />
                                      )}
                                    </Box>
                                  ) : null}
                                </Th>
                              )
                            })}
                          </Tr>
                        )
                      })}
                    </Thead>
                    <Tbody {...getTableBodyProps()}>
                      {!loadingLogs ? (
                        rows.length ? (
                          page.map(row => {
                            prepareRow(row)
                            const { key, ...restRowProps } = row.getRowProps()
                            restRowProps.style = {
                              background:
                                row.index === logDisplayIndex
                                  ? '#F7FAFC'
                                  : 'white',
                            }
                            return (
                              <Tr
                                key={key}
                                {...restRowProps}
                                onClick={() =>
                                  void setLogDisplayIndex(row.index)
                                }
                              >
                                {row.cells.map(cell => {
                                  const { key: keyRow, ...restCellProps } =
                                    cell.getCellProps()
                                  return (
                                    <Td key={keyRow} {...restCellProps}>
                                      <ChakraText>
                                        {cell.render('Cell')}
                                      </ChakraText>
                                    </Td>
                                  )
                                })}
                              </Tr>
                            )
                          })
                        ) : (
                          <Tr>
                            <Td colSpan={6} textAlign="center">
                              <ChakraText>
                                {t('no_logs_available', 'No logs available')}
                              </ChakraText>
                            </Td>
                          </Tr>
                        )
                      ) : (
                        <Tr>
                          <Td colSpan={6} textAlign="center">
                            <Spinner
                              thickness="4px"
                              speed="0.65s"
                              emptyColor="appPaleGrey"
                              color="appBlue"
                              size="xl"
                            />
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </Flex>
                {logs.length ? (
                  <Box w="100%" paddingTop={4}>
                    <Pagination
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      canPreviousPage={canPreviousPage}
                      canNextPage={canNextPage}
                      pageCount={pageCount}
                      pageOptions={pageOptions}
                      setPageSize={setPageSize}
                      previousPage={previousPage}
                      nextPage={nextPage}
                      gotoPage={gotoPage}
                    />
                  </Box>
                ) : null}
              </VStack>
              {!loadingLogs ? (
                logs.length ? (
                  <VStack
                    maxH="75vh"
                    overflow="scroll"
                    alignItems="flex-start"
                    paddingLeft={4}
                    paddingRight={4}
                    w="100%"
                  >
                    <Heading
                      as="h1"
                      size="md"
                      fontWeight="semibold"
                      color="appDarkIndigo"
                      paddingY={4}
                    >
                      {logs[logDisplayIndex].operation}
                    </Heading>
                    <Grid
                      width="100%"
                      templateColumns="repeat(3, minmax(0px, 1fr))"
                      columnGap={5}
                      rowGap={5}
                      paddingBottom={4}
                    >
                      <GridItem>
                        <ChakraText fontWeight="medium">Status</ChakraText>
                      </GridItem>
                      <GridItem colSpan={2}>
                        <StatusBadge
                          statusCode={logs[logDisplayIndex].statusCode}
                          fontSize="1rem"
                          fontWeight="normal"
                        />
                      </GridItem>
                      <GridItem>
                        <ChakraText fontWeight="medium">Method</ChakraText>
                      </GridItem>
                      <GridItem colSpan={2}>
                        <ChakraText>{logs[logDisplayIndex].method}</ChakraText>
                      </GridItem>
                      <GridItem>
                        <ChakraText fontWeight="medium">Endpoint</ChakraText>
                      </GridItem>
                      <GridItem colSpan={2}>
                        <ChakraText>
                          {logs[logDisplayIndex].endpoint}
                        </ChakraText>
                      </GridItem>
                      <GridItem>
                        <ChakraText fontWeight="medium">Request ID</ChakraText>
                      </GridItem>
                      <GridItem colSpan={2}>
                        <Hover>
                          {isHover => (
                            <Flex>
                              <ChakraText>
                                {logs[logDisplayIndex].requestId}
                              </ChakraText>
                              {isHover ? (
                                <CopyIcon
                                  cursor="pointer"
                                  marginLeft="10px"
                                  onClick={() => {
                                    void setCopyRequestId(
                                      logs[logDisplayIndex].requestId,
                                    )
                                  }}
                                />
                              ) : (
                                <Box minW="24px" />
                              )}
                            </Flex>
                          )}
                        </Hover>
                      </GridItem>
                      <GridItem>
                        <ChakraText fontWeight="medium">Time</ChakraText>
                      </GridItem>
                      <GridItem colSpan={2}>
                        <Tooltip
                          label={
                            <>
                              <ChakraText>
                                {moment(logs[logDisplayIndex].timeStamp)
                                  .local()
                                  .format('ddd, MMMM Do YYYY, HH:mm:ss')}{' '}
                                (local)
                              </ChakraText>
                              <ChakraText>
                                {moment
                                  .utc(logs[logDisplayIndex].timeStamp)
                                  .format('ddd, MMMM Do YYYY, HH:mm:ss')}{' '}
                                (UTC)
                              </ChakraText>
                            </>
                          }
                        >
                          <ChakraText>
                            {moment(logs[logDisplayIndex].timeStamp)
                              .local()
                              .format('ddd, MMMM Do YYYY, HH:mm:ss')}
                          </ChakraText>
                        </Tooltip>
                      </GridItem>
                      <GridItem>
                        <ChakraText fontWeight="medium">IP address</ChakraText>
                      </GridItem>
                      <GridItem colSpan={2}>
                        <ChakraText>
                          {logs[logDisplayIndex].ipAddress}
                        </ChakraText>
                      </GridItem>
                    </Grid>
                    <VStack alignItems="flex-start" maxW="100%" minW="100%">
                      {logs[logDisplayIndex].requestBody && (
                        <>
                          <Divider />
                          <Heading
                            as="h1"
                            size="md"
                            fontWeight="semibold"
                            color="appDarkIndigo"
                            paddingY={4}
                          >
                            Request Body
                          </Heading>
                          <Box
                            bg="rgb(25, 48, 85)"
                            color="#878b8e"
                            paddingBottom="10px"
                            width="100%"
                          >
                            <ReactSyntaxHighlighterWrapper
                              language="json"
                              style={vs2015}
                              wrapLines={true}
                              customStyle={codeBlockStyles}
                              showLineNumbers={false}
                              codeTagProps={{
                                style: {
                                  fontFamily: 'monospace,monospace !important',
                                },
                              }}
                            >
                              {JSON.stringify(
                                logs[logDisplayIndex].requestBody,
                                null,
                                4,
                              )}
                            </ReactSyntaxHighlighterWrapper>
                          </Box>
                        </>
                      )}

                      {logs[logDisplayIndex].responseBody && (
                        <>
                          <Heading
                            as="h1"
                            size="md"
                            fontWeight="semibold"
                            color="appDarkIndigo"
                            paddingY={4}
                          >
                            Response Body
                          </Heading>
                          <Box
                            bg="rgb(25, 48, 85)"
                            color="#878b8e"
                            paddingBottom="10px"
                            paddingRight="20px"
                            width="100%"
                          >
                            <ReactSyntaxHighlighterWrapper
                              language="json"
                              style={vs2015}
                              wrapLines={true}
                              customStyle={codeBlockStyles}
                              showLineNumbers={false}
                              codeTagProps={{
                                style: {
                                  fontFamily: 'monospace,monospace !important',
                                },
                              }}
                            >
                              {JSON.stringify(
                                logs[logDisplayIndex].responseBody,
                                null,
                                4,
                              )}
                            </ReactSyntaxHighlighterWrapper>
                          </Box>
                        </>
                      )}
                    </VStack>
                  </VStack>
                ) : (
                  <Center width="full" height="full">
                    <ChakraText>
                      {t('nothing_to_display', 'Nothing to display')}
                    </ChakraText>
                  </Center>
                )
              ) : (
                <Center width="full" height="100%">
                  <Spinner
                    thickness="4px"
                    speed="0.65s"
                    emptyColor="appPaleGrey"
                    color="appBlue"
                    size="xl"
                  />
                </Center>
              )}
            </HStack>
          </>
        ) : (
          <CustomPlanCTA feature={FeaturesEnum.CENTRAL_AUDIT_LOGS} />
        )}
      </MainContentWrapper>
    </BaseLayout>
  )
}

export default AuditLogsLayout
